/**
 * Product Card Variant Selection
 * Matches reference website behavior: allows selecting variants directly on product cards
 * and updating the Add to Cart button, price, image, and form variant ID.
 */

if (!customElements.get('product-card-variants')) {
  customElements.define(
    'product-card-variants',
    class ProductCardVariants extends HTMLElement {
      constructor() {
        super();
        this.hoverTimeout = null;
      }

      connectedCallback() {
        this.card = this.closest('.product-card');
        if (!this.card) return;

        this.form = this.card.querySelector('form[data-type="add-to-cart-form"]');
        this.variantInput = this.form ? this.form.querySelector('[name="id"]') : null;
        this.submitBtn = this.form ? this.form.querySelector('button[type="submit"]') : null;
        this.mainImage = this.card.querySelector('.product-card__image--main img');
        this.priceWrapper = this.card.querySelector('.f-price');
        this.productLinks = this.card.querySelectorAll('a[href*="/products/"]');

        const jsonEl = this.querySelector('.product-card__variants-json');
        if (jsonEl) {
          try {
            this.variants = JSON.parse(jsonEl.textContent);
          } catch (e) {
            console.error('Error parsing product card variants JSON', e);
            this.variants = [];
          }
        } else {
          this.variants = [];
        }

        // Store the initially selected variant image
        const activeSwatch = this.querySelector('.product-card__swatch-btn.is-active');
        if (activeSwatch && activeSwatch.dataset.variantImage) {
          this.currentImageSrc = activeSwatch.dataset.variantImage;
          this.currentImageSrcset = '';
          if (this.mainImage && (!this.mainImage.getAttribute('src') || this.mainImage.getAttribute('src') !== activeSwatch.dataset.variantImage)) {
            this.mainImage.src = activeSwatch.dataset.variantImage;
            this.mainImage.srcset = '';
          }
        } else if (this.mainImage) {
          this.currentImageSrc = this.mainImage.getAttribute('src');
          this.currentImageSrcset = this.mainImage.getAttribute('srcset') || '';
        }

        // Hide pills toggle if all options already fit on a single line
        const pillGroups = this.querySelectorAll('.product-card__option-group--pills');
        pillGroups.forEach((group) => {
          const pillsEl = group.querySelector('.product-card__option-pills');
          const toggleBtn = group.querySelector('.product-card__pills-toggle');
          if (pillsEl && toggleBtn) {
            requestAnimationFrame(() => {
              if (pillsEl.scrollHeight <= 30) {
                toggleBtn.style.display = 'none';
              }
            });
          }
        });

        this.initListeners();
      }

      initListeners() {
        // Swatch & Pill button clicks
        this.addEventListener('click', (event) => {
          const btn = event.target.closest('.product-card__swatch-btn, .product-card__pill-btn');
          if (!btn) return;

          event.preventDefault();
          event.stopPropagation();

          this.handleOptionClick(btn);
        });

        // Pills show more / show less toggle
        this.addEventListener('click', (event) => {
          const toggleBtn = event.target.closest('.product-card__pills-toggle');
          if (!toggleBtn) return;

          event.preventDefault();
          event.stopPropagation();

          const group = toggleBtn.closest('.product-card__option-group--pills');
          if (!group) return;

          const isExpanded = group.classList.toggle('is-expanded');
          toggleBtn.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
          const textEl = toggleBtn.querySelector('.product-card__pills-toggle-text');
          if (textEl) {
            textEl.textContent = isExpanded ? 'Show less' : 'Show more';
          }

          const pillsEl = group.querySelector('.product-card__option-pills');
          if (pillsEl) {
            if (isExpanded) {
              pillsEl.scrollTop = 0;
            } else {
              const activeBtn = group.querySelector('.product-card__pill-btn.is-active');
              if (activeBtn && activeBtn.offsetTop > 0) {
                pillsEl.scrollTop = activeBtn.offsetTop;
              } else {
                pillsEl.scrollTop = 0;
              }
            }
          }
        });

        // Hover image preview on color swatches
        const swatchBtns = this.querySelectorAll('.product-card__swatch-btn');
        swatchBtns.forEach((btn) => {
          btn.addEventListener('mouseenter', () => {
            const previewImg = btn.dataset.variantImage;
            if (previewImg && this.mainImage) {
              clearTimeout(this.hoverTimeout);
              this.hoverTimeout = setTimeout(() => {
                this.mainImage.src = previewImg;
                this.mainImage.srcset = '';
              }, 40);
            }
          });

          btn.addEventListener('mouseleave', () => {
            clearTimeout(this.hoverTimeout);
            // Revert back to the actively selected variant image
            if (this.currentImageSrc && this.mainImage) {
              this.mainImage.src = this.currentImageSrc;
              if (this.currentImageSrcset) {
                this.mainImage.srcset = this.currentImageSrcset;
              } else {
                this.mainImage.srcset = '';
              }
            }
          });
        });
      }

      handleOptionClick(clickedBtn) {
        clearTimeout(this.hoverTimeout);
        const optionGroup = clickedBtn.closest('.product-card__option-group');
        if (!optionGroup) return;

        // 1. Update active state inside this option group
        optionGroup.querySelectorAll('.product-card__swatch-btn, .product-card__pill-btn').forEach((btn) => {
          btn.classList.remove('is-active');
          btn.setAttribute('aria-selected', 'false');
        });
        clickedBtn.classList.add('is-active');
        clickedBtn.setAttribute('aria-selected', 'true');

        // 2. Gather selected values across all groups
        const selectedOptions = [];
        this.querySelectorAll('.product-card__option-group').forEach((group) => {
          const activeEl = group.querySelector('.is-active, [aria-selected="true"]');
          if (activeEl && activeEl.dataset.value) {
            const idx = parseInt(group.dataset.optionIndex, 10);
            selectedOptions[idx] = activeEl.dataset.value;
          }
        });

        // 3. Find matching variant from this.variants
        let matchedVariant = null;
        if (clickedBtn.dataset.variantId) {
          matchedVariant = this.variants.find((v) => String(v.id) === String(clickedBtn.dataset.variantId));
        }
        if (!matchedVariant) {
          matchedVariant = this.variants.find((v) => {
            if (!v.options || !Array.isArray(v.options)) return false;
            return v.available && v.options.every((optVal, idx) => {
              return selectedOptions[idx] === undefined || optVal === selectedOptions[idx];
            });
          });
        }
        if (!matchedVariant) {
          matchedVariant = this.variants.find((v) => {
            if (!v.options || !Array.isArray(v.options)) return false;
            return v.options.every((optVal, idx) => {
              return selectedOptions[idx] === undefined || optVal === selectedOptions[idx];
            });
          });
        }
        if (!matchedVariant && clickedBtn.dataset.value) {
          const clickedIdx = parseInt(optionGroup.dataset.optionIndex, 10);
          matchedVariant = this.variants.find((v) => v.available && v.options[clickedIdx] === clickedBtn.dataset.value)
            || this.variants.find((v) => v.options[clickedIdx] === clickedBtn.dataset.value);
        }

        if (matchedVariant) {
          // Sync all groups' active classes to match matchedVariant.options
          this.querySelectorAll('.product-card__option-group').forEach((group) => {
            const idx = parseInt(group.dataset.optionIndex, 10);
            const targetVal = matchedVariant.options ? matchedVariant.options[idx] : null;
            if (targetVal) {
              group.querySelectorAll('.product-card__swatch-btn, .product-card__pill-btn').forEach((btn) => {
                const isMatch = btn.dataset.value === targetVal;
                btn.classList.toggle('is-active', isMatch);
                btn.setAttribute('aria-selected', isMatch ? 'true' : 'false');
              });
            }
          });

          this.updateCardState(matchedVariant);
        }
      }

      updateCardState(variant) {
        // 1. Update Hidden Form Input
        if (this.variantInput) {
          this.variantInput.value = variant.id;
        }

        // 2. Update Submit Button
        if (this.submitBtn) {
          const atcText = this.submitBtn.querySelector('.product-card__atc-text');
          if (variant.available) {
            this.submitBtn.disabled = false;
            this.submitBtn.removeAttribute('disabled');
            this.submitBtn.removeAttribute('aria-disabled');
            if (this.variantInput) {
              this.variantInput.disabled = false;
              this.variantInput.removeAttribute('disabled');
            }
            if (atcText) atcText.textContent = this.dataset.addText || 'Add to Cart';
          } else {
            this.submitBtn.disabled = true;
            this.submitBtn.setAttribute('disabled', 'true');
            this.submitBtn.setAttribute('aria-disabled', 'true');
            if (this.variantInput) {
              this.variantInput.disabled = true;
              this.variantInput.setAttribute('disabled', 'true');
            }
            if (atcText) atcText.textContent = this.dataset.soldOutText || 'Sold Out';
          }
        }

        // 3. Update Main Image
        if (variant.image && this.mainImage) {
          this.mainImage.src = variant.image;
          this.mainImage.srcset = '';
          this.currentImageSrc = variant.image;
          this.currentImageSrcset = '';
        }

        // 4. Update Price
        if (this.priceWrapper && variant.price !== undefined) {
          this.updatePrice(variant);
        }

        // 5. Update Product Links (?variant=...)
        if (this.productLinks && variant.id) {
          this.productLinks.forEach((link) => {
            try {
              const url = new URL(link.href, window.location.origin);
              url.searchParams.set('variant', variant.id);
              link.href = url.pathname + url.search;
            } catch (e) {}
          });
        }
      }

      updatePrice(variant) {
        const isSale = variant.compare_at_price > variant.price;
        const moneyFormat = window.FoxTheme && window.FoxTheme.settings ? window.FoxTheme.settings.moneyFormat : '${{amount}}';
        const formatFn = window.FoxTheme && window.FoxTheme.Currency && window.FoxTheme.Currency.formatMoney
          ? window.FoxTheme.Currency.formatMoney
          : (cents) => '$' + (cents / 100).toFixed(2);

        const formattedPrice = formatFn(variant.price, moneyFormat);
        const formattedComparePrice = variant.compare_at_price ? formatFn(variant.compare_at_price, moneyFormat) : '';

        if (isSale) {
          this.priceWrapper.classList.add('f-price--on-sale');
          const salePriceEl = this.priceWrapper.querySelector('.f-price__sale .f-price-item--sale');
          const comparePriceEl = this.priceWrapper.querySelector('.f-price__sale .f-price-item--regular');
          if (salePriceEl) salePriceEl.innerHTML = formattedPrice;
          if (comparePriceEl) comparePriceEl.innerHTML = `<s>${formattedComparePrice}</s>`;
        } else {
          this.priceWrapper.classList.remove('f-price--on-sale');
          const regularPriceEl = this.priceWrapper.querySelector('.f-price__regular .f-price-item--regular');
          if (regularPriceEl) regularPriceEl.innerHTML = formattedPrice;
        }
      }
    }
  );
}
